import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import Calendar from "react-calendar";
import { toast } from "react-toastify";

import "react-datepicker/dist/react-datepicker.css";
import "react-calendar/dist/Calendar.css";
import "react-toastify/dist/ReactToastify.css";

import API_BASE_URL from "../../../src/config";

const RoomAvailability = forwardRef(({ stayId: propStayId }, ref) => {

  const [rooms, setRooms] = useState([]);
  const [openRoom, setOpenRoom] = useState(null);

  const [roomAvailability, setRoomAvailability] = useState({});
  const [roomDateRange, setRoomDateRange] = useState({});
  const [roomAvailable, setRoomAvailable] = useState({});

  const [roomIcalUrl, setRoomIcalUrl] = useState({});
  const [roomPlatform, setRoomPlatform] = useState({});

  const [roomCalendars, setRoomCalendars] = useState({});

  const stayId = propStayId || localStorage.getItem("stayId");
  const [roomExportUrls, setRoomExportUrls] = useState({});

  /* --------------------------------------------------- */
  /* FETCH ROOMS                                         */
  /* --------------------------------------------------- */

  useEffect(() => {
    fetchRooms();
  }, [stayId]);

  const fetchRooms = async () => {

    if (!stayId) return;

    try {

      const token = localStorage.getItem("accessToken") || "";

      const res = await axios.post(
        `${API_BASE_URL}stays/details`,
        { id: stayId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {

        const roomsData = res.data.data.rooms || [];

        setRooms(roomsData);

        /* open first room automatically */

        if (roomsData.length > 0) {
          setOpenRoom(roomsData[0].id);
        }

        let availabilityMap = {};

        roomsData.forEach((room) => {

          fetchRoomCalendars(room.id);
          fetchRoomExportUrl(room.id);
          availabilityMap[room.id] = (room.availability || []).map((a) => ({
            date: a.date,
            rooms_available: Number(a.rooms_available || 0),
            bookingplatform: (a.bookingplatform || "").trim()
          }));

        });

        setRoomAvailability(availabilityMap);

      }

    } catch (err) {

      console.error(err);

    }

  };

  /* --------------------------------------------------- */
  /* FETCH CALENDARS                                     */
  /* --------------------------------------------------- */

  const fetchRoomCalendars = async (roomId) => {

    try {

      const token = localStorage.getItem("accessToken");

      const res = await axios.get(
        `${API_BASE_URL}stays/ical/import-calendar/${roomId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data && res.data.data) {

        setRoomCalendars((prev) => ({
          ...prev,
          [roomId]: res.data.data
        }));

      }

    } catch (error) {

      console.error("Calendar fetch error", error);

    }

  };

  /* --------------------------------------------------- */
  /* DELETE CALENDAR                                     */
  /* --------------------------------------------------- */

  const deleteCalendar = async (calendarId, roomId) => {

    if (!window.confirm("Delete this calendar?")) return;

    try {

      const token = localStorage.getItem("accessToken");

      await axios.delete(
        `${API_BASE_URL}stays/ical/import-calendar/${calendarId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Calendar deleted");

      fetchRoomCalendars(roomId);

    } catch {

      toast.error("Delete failed");

    }

  };

  const fetchRoomExportUrl = async (roomId) => {

    try {

      const token = localStorage.getItem("accessToken");

      const res = await axios.get(
        `${API_BASE_URL}stays/ical/link/${roomId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {

        setRoomExportUrls((prev) => ({
          ...prev,
          [roomId]: res.data.data.ical_url
        }));

      }

    } catch (error) {

      console.error("Export URL fetch error", error);

    }

  };
  /* --------------------------------------------------- */
  /* SAVE AVAILABILITY                                   */
  /* --------------------------------------------------- */

  const handleSubmitAvailability = async (roomId) => {

    const startDate = roomDateRange[roomId]?.[0];
    const endDate = roomDateRange[roomId]?.[1];
    const isAvailable = roomAvailable[roomId];

    if (!startDate || !endDate) {
      toast.warn("Select date range");
      return false;
    }

    if (isAvailable === undefined) {
      toast.warn("Select Available or Booked");
      return false;
    }

    try {

      const token = localStorage.getItem("accessToken");

      await axios.post(
        `${API_BASE_URL}stays/availability`,
        {
          roomId,
          start_date: startDate.toLocaleDateString("en-CA"),
          end_date: endDate.toLocaleDateString("en-CA"),
          is_available: isAvailable
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Availability updated");

      fetchRooms();

      return true;

    } catch {

      toast.error("Update failed");
      return false;

    }

  };

  /* --------------------------------------------------- */
  /* ICAL SYNC                                           */
  /* --------------------------------------------------- */

  const saveIcalUrl = async (roomId) => {

    const url = roomIcalUrl[roomId];
    const platform = roomPlatform[roomId];

    if (!url || !platform) {
      toast.warn("Select platform and paste iCal URL");
      return;
    }

    try {

      const token = localStorage.getItem("accessToken");

      await axios.post(
        `${API_BASE_URL}stays/ical/import`,
        {
          stay_room_id: roomId,
          platform,
          ical_url: url
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Calendar synced");

      fetchRoomCalendars(roomId);

    } catch (err) {

      toast.error(err?.response?.data?.message || "Sync failed");

    }

  };

  /* --------------------------------------------------- */
  /* SUBMIT FROM PARENT                                  */
  /* --------------------------------------------------- */

  useImperativeHandle(ref, () => ({
    submit: async () => {

      let success = true;

      for (const room of rooms) {

        const changed = roomDateRange[room.id];

        if (!changed) continue;

        const ok = await handleSubmitAvailability(room.id);

        if (!ok) success = false;

      }

      return success;

    },
  }));

  /* --------------------------------------------------- */
  /* UI                                                  */
  /* --------------------------------------------------- */
  const copyUrl = (url) => {

    navigator.clipboard.writeText(url);

    toast.success("Export URL copied");

  };
  return (

    <div className="availability-wrapper">

      <h2 className="page-title">Room Availability</h2>

      {rooms.map((room) => {

        const startDate = roomDateRange[room.id]?.[0] || null;
        const endDate = roomDateRange[room.id]?.[1] || null;

        return (

          <div key={room.id} className="room-card">

            <div
              className="room-header"
              onClick={() =>
                setOpenRoom(openRoom === room.id ? null : room.id)
              }
            >

              <div className="room-title">

                {openRoom === room.id ? "▼" : "►"} {room.title} ({room.room_type})

              </div>

            </div>

            {openRoom === room.id && (

              <div className="room-body">

                <div className="room-layout">

                  {/* LEFT SIDE */}

                  <div className="calendar-section">


                    <h4>GuestLinkUp Export Calendar</h4>

                    <div className="export-box">

                      <input
                        type="text"
                        readOnly
                        value={roomExportUrls[room.id] || ""}
                      />

                      <button
                        className="copy-btn"
                        onClick={() => copyUrl(roomExportUrls[room.id])}
                      >
                        Copy
                      </button>

                    </div>

                    <div className="date-row">

                      <DatePicker
                        selected={startDate}
                        onChange={(date) =>
                          setRoomDateRange({
                            ...roomDateRange,
                            [room.id]: [date, endDate]
                          })
                        }
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        minDate={new Date()}
                        placeholderText="Start Date"
                        dateFormat="dd/MM/yyyy"
                      />

                      <DatePicker
                        selected={endDate}
                        onChange={(date) =>
                          setRoomDateRange({
                            ...roomDateRange,
                            [room.id]: [startDate, date]
                          })
                        }
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        placeholderText="End Date"
                        dateFormat="dd/MM/yyyy"
                      />

                    </div>

                    <div className="radio-row">

                      <label>
                        <input
                          type="radio"
                          name={`status-${room.id}`}
                          checked={roomAvailable[room.id] === true}
                          onChange={() =>
                            setRoomAvailable({
                              ...roomAvailable,
                              [room.id]: true
                            })
                          }
                        />
                        Available
                      </label>

                      <label>
                        <input
                          type="radio"
                          name={`status-${room.id}`}
                          checked={roomAvailable[room.id] === false}
                          onChange={() =>
                            setRoomAvailable({
                              ...roomAvailable,
                              [room.id]: false
                            })
                          }
                        />
                        Block / Booked
                      </label>

                    </div>

                    <Calendar
                      className="calendar"
                      tileContent={({ date }) => {

                        const iso = date.toLocaleDateString("en-CA");

                        const apiData =
                          roomAvailability[room.id]?.find(
                            (d) => d.date === iso
                          );

                        console.log("API Data for", iso, apiData);

                        if (!apiData) return null;

                        const roomsAvailable = Number(apiData.rooms_available || 0);
                        const platform = (apiData.bookingplatform || "").trim();

                        let label = "";
                        let className = "badge";

                        //. RULE 1: Available
                        if (roomsAvailable > 0) {
                          label = `A/${roomsAvailable}`;
                          className += " available";
                        }

                        //. RULE 2: Booked (platform exists)
                        else if (roomsAvailable === 0 && platform) {
                          // label = `Booked (${platform})`;
                          return (
                            <span className="badge booked p-1">
                              <div>Booked</div>
                              <div className="platform-name">{platform}</div>
                            </span>
                          );
                          className += " booked";
                        }

                        //. RULE 3: Blocked
                        else {
                          label = "Blocked";
                          className += " blocked";
                        }

                        return (
                          <span className={className}>
                            {label}
                          </span>
                        );

                      }}
                    />

                  </div>

                  {/* RIGHT SIDE PANEL */}

                  <div className="sync-panel">

                    <h4>Import External Calendar Sync</h4>
                    <p className="sync-panel-help">
                      Use your platform's <strong>iCal / ICS export link</strong> (found in that platform's calendar settings) — not your listing's public page URL.
                    </p>

                    <select
                      value={roomPlatform[room.id] || ""}
                      onChange={(e) =>
                        setRoomPlatform({
                          ...roomPlatform,
                          [room.id]: e.target.value
                        })
                      }
                    >
                      <option value="">Select Platform</option>
                      <option value="airbnb">Airbnb</option>
                      <option value="booking">Booking.com</option>
                      <option value="vrbo">VRBO</option>
                      <option value="agoda">Agoda</option>
                      <option value="other">Other</option>
                    </select>

                    <input
                      type="text"
                      placeholder="Paste iCal/ICS export URL (not the listing page link)"
                      value={roomIcalUrl[room.id] || ""}
                      onChange={(e) =>
                        setRoomIcalUrl({
                          ...roomIcalUrl,
                          [room.id]: e.target.value
                        })
                      }
                    />

                    <button onClick={() => saveIcalUrl(room.id)}>
                      Sync
                    </button>

                    <div className="calendar-list">

                      {(roomCalendars[room.id] || []).map((cal) => (

                        <div key={cal.id} className="calendar-item">

                          <div>
                            <span className="platform">{cal.platform}</span>
                            <div className="url">{cal.ical_url}</div>
                          </div>

                          <button
                            onClick={() =>
                              deleteCalendar(cal.id, room.id)
                            }
                          >
                            Delete
                          </button>

                        </div>

                      ))}

                    </div>

                  </div>

                </div>

              </div>

            )}

          </div>

        );

      })}

      <style jsx>{`

.availability-wrapper{
max-width:1200px;
margin:auto;
font-family:Inter;
}

.room-header{
padding:16px;
cursor:pointer;
font-weight:600;
}

.room-body{
padding:20px;
border-top:1px solid #eee;
}


.sync-panel input,
.sync-panel select{
width:100%;
margin-bottom:10px;
padding:8px;
border:1px solid #ddd;
border-radius:6px;
}

.radio-row{
display:flex;
gap:40px;
margin:20px 0;
font-size:14px;
}

.sync-panel button{
background:#2ca65a;
color:white;
border:none;
padding:8px 14px;
border-radius:6px;
cursor:pointer;
}

.calendar-item{
display:flex;
justify-content:space-between;
padding:8px;
border:1px solid #eee;
border-radius:6px;
margin-top:6px;
}

.badge{
font-size:10px;
padding:2px 5px;
border-radius:4px;
display:block;
text-align:center;
}

.available{
background:#e7f7ee;
color:#1e8e4d;
}

.booked{
padding: 5px 0px !important;
font-size: 10px;
background:#fdeaea;
color:#d93025;
}

.blocked{
  background: #fef9c3;
  color: #ca8a04;
}

.react-calendar {
width: 100% !important;
}
.date-row{
display:flex;
gap:20px;
margin-bottom:20px;
}

.date-row :global(.react-datepicker-wrapper){
width:100%;
}
.date-row input{
width:100%;
padding:10px;
border:1px solid #ddd;
border-radius:6px;
}

.export-box{
display:flex;
gap:10px;
margin-bottom:15px;
}

.export-box input{
flex:1;
padding:8px;
border:1px solid #ddd;
border-radius:6px;
font-size:13px;
}

.copy-btn{
background:#2563eb;
color:white;
border:none;
padding:8px 14px;
border-radius:6px;
cursor:pointer;
}

.platform-name{
  font-size: 9px;
  margin-top: 2px;
  color: gray;
}
`}</style>

    </div>

  );

});

export default RoomAvailability;